import React, { useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik, FormikProvider, Field } from 'formik';
import * as Yup from 'yup';
import { YesNoCodesEnum } from 'app/bff/enums/yesNo';
// import { UserTypesEnum } from 'app/bff/enums/user';
import { ROUTES } from 'app/core/router/paths';
import commonService from 'app/core/services/commonService';
import modalService from 'app/core/services/modalService';
import validationService from 'app/core/services/validationService';
import { ModalNamesEnum } from 'app/core/enums/ui/modals';
import { RootState } from 'app/store/types';
import { fetchCaptchaAction, loginByMobileLoginAction, loginByPassPhraseLoginAction, resendOTPAction, loginByOTPAction, resetLoginCacheAction } from 'app/store/features/member/login/actions';
import FormikForm from 'app/common/components/Form/FormikForm';
import Modal from 'app/common/components/Modals/Modal';
import Tabs from 'app/common/components/Tabs';
import InputTextField from 'app/common/components/Form/Fields/Common/InputTextField';
import InputPasswordField from 'app/common/components/Form/Fields/Common/InputPasswordField';
import CaptchaField from 'app/common/components/Form/Fields/Custom/CaptchaField';
import OTPField from 'app/common/components/Form/Fields/Custom/OTPField';
import { MemberLoginModalProps, LoginModesEnum, MobileLoginFormValues, MobileLoginOTPAuthFormValues, MobileLoginProcessStepCodesEnum, PassPhraseLoginFormValues } from './types';
import InputPasswordField01 from 'app/common/components/Form/Fields/Common/InputPasswordField01';

const MemberLoginModal: React.FC<MemberLoginModalProps> = (props) => {
  const routerHistory = useHistory();
  const reduxDispatch = useDispatch();
  const loginState = useSelector((state: RootState) => state.features.member.login);

  // 手機驗證登入 (Step-1) Formik Config
  const mobileLoginFormik = useFormik<MobileLoginFormValues>({
    initialValues: {
      memberId: '',
      captchaCode: '',
      euMark: YesNoCodesEnum.No
    },
    validationSchema: Yup.object().shape({
      memberId: Yup.string().required().concat(validationService.personalIdSchema),
      captchaCode: Yup.string().required().max(6)
    }),
    onSubmit: (formValues) => {
      if (!loginState.static.captcha) return;
      // 取得會員資料 (取得成功後，執行驗證圖形驗證碼)
      const { captchaSn } = loginState.static.captcha;
      const { captchaCode, memberId, euMark } = formValues;
      const checkCaptchaArgs = { captchaSn, captchaCode };
      const authMemberArgs = { actionType: 'OTP', userType: 'member', birthday: '', action: '1', memberId: memberId, euMark: euMark };
      reduxDispatch(loginByMobileLoginAction(checkCaptchaArgs, authMemberArgs));
      // 清除表單值
      mobileLoginFormik.resetForm();
    }
  });

  // 手機驗證登入 (Step-2) Formik Config
  const mobileLoginOTPAuthFormik = useFormik<MobileLoginOTPAuthFormValues>({
    initialValues: {
      otpCode: ''
    },
    validationSchema: Yup.object().shape({
      otpCode: Yup.string().required().concat(validationService.OTPCodeSchema)
    }),
    onSubmit: (formValues) => {
      if (!loginState.mobileLoginProcess.data || !loginState.mobileLoginProcess.otp) return;
      const { euMark } = loginState.mobileLoginProcess.data.data;
      const { sn } = loginState.mobileLoginProcess.otp;
      const { otpCode } = formValues;
      if (sn) {
        reduxDispatch(loginByOTPAction({ sn, otpCode, euMark }));
      }
    }
  });

  /**
   * @description 密碼登入 Formik Config
   */
  const passPhraseLoginFormik = useFormik<PassPhraseLoginFormValues>({
    initialValues: {
      memberId: '',
      passPhrase: '',
      captchaCode: '',
      euMark: YesNoCodesEnum.No
    },
    validationSchema: Yup.object().shape({
      memberId: Yup.string().required().max(50),
      passPhrase: Yup.string().required(),
      captchaCode: Yup.string().required().max(6)
    }),
    onSubmit: (formValues) => {
      if (!loginState.static.captcha) return;
      // 取得會員資料 (取得成功後，執行驗證圖形驗證碼)
      const { captchaSn } = loginState.static.captcha;
      const { captchaCode, memberId, passPhrase, euMark } = formValues;
      const checkCaptchaArgs = { captchaSn, captchaCode };
      const authMemberArgs = { actionType: 'ACC', userType: 'member', birthday: '', action: 'Y', userId: memberId, pswd: passPhrase, euMark };
      reduxDispatch(loginByPassPhraseLoginAction(checkCaptchaArgs, authMemberArgs));
      // 清除表單值
      passPhraseLoginFormik.resetForm();
    }
  });

  /**
   * @description 取得圖形驗證碼
   */
  const fetchCaptcha = useCallback(() => {
    reduxDispatch(fetchCaptchaAction());
  }, [reduxDispatch]);

  /**
   * @description 組件初始化後執行的 Effect
   */
  useEffect(() => {
    // 取得圖形驗證碼
    fetchCaptcha();
  }, [fetchCaptcha]);

  /**
   * @description 手機驗證登入 (Step-2) 處理「返回」按鈕執行的事件
   */
  const handleMobileOTPAuthStepBack = () => {
    // 重置登入流程資料緩存
    reduxDispatch(resetLoginCacheAction());
    // 取得圖形驗證碼
    fetchCaptcha();
    // 清除表單值
    mobileLoginOTPAuthFormik.resetForm();
  };

  /**
   * @description 重新發送驗證碼
   */
  const handleResendOTP = () => {
    if (!loginState.mobileLoginProcess.data) return;
    const captcha = {
      captchaSn: loginState.static.captcha?.captchaSn ?? '',
      captchaCode: loginState.static.captcha?.captchaCode ?? '',
      captchaImg: loginState.static.captcha?.captchaImg ?? ''
    };
    const data = loginState.mobileLoginProcess.data.data;
    if (captcha.captchaSn && captcha.captchaImg && data) {
      reduxDispatch(resendOTPAction(captcha, data));
    }
  };

  /**
   * @description 導頁至註冊流程
   */
  const handleRedirectToRegisterProcess = () => {
    modalService.login(false);
    routerHistory.push(ROUTES.MEMBER__REGISTER__AUTH);
  };

  /**
   * @description 導頁至忘記密碼流程
   */
  const handleRedirectToForgetProcess = () => {
    modalService.login(false);
    routerHistory.push(ROUTES.MEMBER__FORGET__AUTH);
  };

  /**
   * @description 處理關閉 Modal
   */
  const handleCloseModal = () => {
    // 重置登入流程資料緩存
    reduxDispatch(resetLoginCacheAction());
    // 清除表單值
    mobileLoginFormik.resetForm();
    mobileLoginOTPAuthFormik.resetForm();
  };

  return (
    <Modal
      name={ModalNamesEnum.MemberLoginModal}
      className="member-login-modal"
      title="會員登入"
      visible={props.visible}
      onClose={handleCloseModal}
    >
      <Tabs navsVisible={!loginState.mobileLoginProcess.otp}>
        <Tabs.Panel name={LoginModesEnum.Mobile} title="手機驗證登入">
          {/* 手機驗證登入 (Step-1) */}
          {loginState.mobileLoginProcess.currentStep === MobileLoginProcessStepCodesEnum.Login && (
            <FormikProvider value={mobileLoginFormik}>
              <FormikForm>
                <div className="login-inputs-container m-0">
                  {/* 帳號 */}
                  <InputPasswordField01
                    name="memberId"
                    label="身分證字號"
                    wrapClassName="item"
                    placeholder="請輸入身分證字號"
                    title="身分證字號"
                  />
                  {/* 圖形驗證碼 */}
                  <CaptchaField
                    name="captchaCode"
                    wrapClassName="item"
                    code={loginState.static.captcha?.captchaCode ?? ''}
                    captchaImg={loginState.static.captcha?.captchaImg ?? ''}
                    captchaSn={loginState.static.captcha?.captchaSn}
                    onRefresh={fetchCaptcha}
                  />
                  <div className="col-12 item">
                    <div className="form-check mb-3">
                      <Field type="radio" name="euMark" id="mobile-login-euMark-radio-0" className="form-check-input me-2" value={YesNoCodesEnum.No} />
                      <label className="form-check-label mt-1" htmlFor="mobile-login-euMark-radio-0">本人目前未具有歐盟會員國之國籍，且目前未居住於歐盟地區</label>
                    </div>
                    <div className="form-check">
                      <Field type="radio" name="euMark" id="mobile-login-euMark-radio-1" className="form-check-input me-2" value={YesNoCodesEnum.Yes} />
                      <label className="form-check-label mt-1" htmlFor="mobile-login-euMark-radio-1">本人目前具有歐盟會員國之國籍，或目前居住於歐盟地區</label>
                    </div>
                  </div>
                  <div className="col-12 item">
                    <div className="clarify">本人同意貴公司蒐集 IP 且判斷其所在地區是否為歐盟地區 IP，註記是否為歐盟自然人</div>
                    <div className="notes">*註：本公司現行保險商品與服務，未有意圖針對歐盟市場進行提供與推廣，亦未於歐盟地區設立服務據點，非受 GDPR 重點影響之對象，僅基於管理需要，鑑別網路用戶來源是否為歐盟地區，並不會利用於影響用戶決策行為之分析使用。</div>
                  </div>
                  <button title="前往「OTP 驗證頁」" type="submit" className="btn btn-primary send-verify-code">發送驗證碼</button>
                  <button title="前往「會員註冊頁」" type="button" className="register-btn" onClick={handleRedirectToRegisterProcess}>
                    <span>註冊成為會員</span>
                    <img src={require('assets/img/icons/arrow-right.svg').default} alt="" />
                  </button>
                </div>
              </FormikForm>
            </FormikProvider>
          )}
          {/* 手機驗證登入 (Step-2) */}
          {loginState.mobileLoginProcess.currentStep === MobileLoginProcessStepCodesEnum.OTPAuth && (
            <FormikProvider value={mobileLoginOTPAuthFormik}>
              <FormikForm>
                <div className="login-inputs-container m-0">
                  <button type="button" className="return" onClick={handleMobileOTPAuthStepBack}>
                    <img src={require('assets/img/icons/chevron-left.svg').default} alt="" />
                    <span>返回</span>
                  </button>
                  <div className="orange-block">驗證碼已發送至手機 {commonService.protectedPhoneNumbers(loginState.mobileLoginProcess.otp?.mobile ?? '')} 及電子信箱 {commonService.protectedEmail(loginState.mobileLoginProcess.otp?.emailAddr ?? '')}，請於 5 分鐘內輸入驗證碼，逾時驗證碼將失效。</div>
                  {/* 驗證碼 */}
                  <OTPField
                    name="otpCode"
                    label="驗證碼"
                    wrapClassName="item"
                    duration={30000}
                    startTimerRef={loginState.mobileLoginProcess.otp?.sn}
                    onResend={handleResendOTP}
                  />
                  <button title="登入" type="submit" className="btn btn-primary w-100">登入</button>
                  <button title="前往「會員註冊頁」" type="button" className="register-btn">
                    <span>註冊成為會員</span>
                    <img src={require('assets/img/icons/arrow-right.svg').default} alt="" />
                  </button>
                </div>
              </FormikForm>
            </FormikProvider>
          )}
        </Tabs.Panel>
        <Tabs.Panel name={LoginModesEnum.PassPhrase} title="密碼登入">
          {/* 密碼登入 */}
          <FormikProvider value={passPhraseLoginFormik}>
            <FormikForm>
              <div className="login-inputs-container m-0">
                <div className="col-12">
                  <div className="notes">*若已為本公司網服會員，請輸入網服帳號，若新使用網投平台註冊，請輸入身分證號。</div>
                </div>
                {/* 帳號 */}
                <InputTextField
                  name="memberId"
                  label="帳號"
                  wrapClassName="item"
                  placeholder="請輸入帳號"
                  aria-label="帳號"
                />
                {/* 密碼 */}
                <InputPasswordField
                  name="passPhrase"
                  label="密碼"
                  wrapClassName="item"
                  placeholder="請輸入密碼"
                  onRedirectToForget={handleRedirectToForgetProcess}
                  disabledShowPassWord
                />
                {/* 圖形驗證碼 */}
                <CaptchaField
                  name="captchaCode"
                  wrapClassName="item"
                  code={loginState.static.captcha?.captchaCode ?? ''}
                  captchaImg={loginState.static.captcha?.captchaImg ?? ''}
                  captchaSn={loginState.static.captcha?.captchaSn}
                  onRefresh={fetchCaptcha}
                />
                <div className="col-12 item">
                  <div className="form-check mb-3">
                    <Field type="radio" name="euMark" id="pass-phrase-login-euMark-radio-0" className="form-check-input me-2" value={YesNoCodesEnum.No} />
                    <label className="form-check-label mt-1" htmlFor="pass-phrase-login-euMark-radio-0">本人目前未具有歐盟會員國之國籍，且目前未居住於歐盟地區</label>
                  </div>
                  <div className="form-check">
                    <Field type="radio" name="euMark" id="pass-phrase-login-euMark-radio-1" className="form-check-input me-2" value={YesNoCodesEnum.Yes} />
                    <label className="form-check-label mt-1" htmlFor="pass-phrase-login-euMark-radio-1">本人目前具有歐盟會員國之國籍，或目前居住於歐盟地區</label>
                  </div>
                </div>
                <div className="col-12 item">
                  <div className="clarify">本人同意貴公司蒐集 IP 且判斷其所在地區是否為歐盟地區 IP，註記是否為歐盟自然人</div>
                  <div className="notes">*註：本公司現行保險商品與服務，未有意圖針對歐盟市場進行提供與推廣，亦未於歐盟地區設立服務據點，非受 GDPR 重點影響之對象，僅基於管理需要，鑑別網路用戶來源是否為歐盟地區，並不會利用於影響用戶決策行為之分析使用。</div>
                </div>
                <button title="立即登入" type="submit" className="btn btn-primary send-verify-code">立即登入</button>
                <button title="前往「會員註冊頁」" type="button" className="register-btn" onClick={handleRedirectToRegisterProcess}>
                  <span>註冊成為會員</span>
                  <img src={require('assets/img/icons/arrow-right.svg').default} alt="" />
                </button>
              </div>
            </FormikForm>
          </FormikProvider>
        </Tabs.Panel>
      </Tabs>
    </Modal>
  );
};

export default MemberLoginModal;
